// src/components/ImportSurveyModal.js
import React, { useState } from 'react';
import { PLATFORMS, VERTICALS, ADS_DESTINATIONS, newPlatform } from '../constants';
import { SURVEY_TYPES, parseQaSurvey } from './surveyParsers';

// ---------- Parsing helpers ----------

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Strip a leading "*" (used throughout the doc for required fields) before comparing headings
function normLine(l) {
  return l.replace(/^\*+\s*/, '').trim().toLowerCase();
}

// Section/prompt headings in this template that never contain a value themselves —
// used so the lookahead never mistakes a heading for the field's value.
const KNOWN_STOP_HEADINGS = [
  'links:', 'pm: deliverables', 'deliverables', 'paid media service channels',
  'provide channel-specific location targeting', 'coupons (lps & meta only)',
  'lsa onboarding', 'lsa information', 'business verifications',
  'ga campaign structure & ad groups', 'services targeted', 'campaign structure',
  'ads schedule (if different by product specify below)', 'location targeting',
  'location/services targeted', 'suggested targeting/addl budget related notes',
  'call tracking setup', 'additional notes',
];

function looksLikeLabel(t) {
  if (!t) return true;
  const norm = normLine(t);
  if (KNOWN_STOP_HEADINGS.includes(norm)) return true;
  if (t.trim().endsWith('?')) return true; // prompts/questions in the template, not answers
  // "Something:" with little/nothing meaningful after it — treat as a new label, not a value
  if (/^[*A-Za-z0-9 /&()'.-]{2,60}:\s*.{0,3}$/.test(t)) return true;
  return false;
}

// Finds a labeled field anywhere in the doc. Handles "Label: value" on one line,
// "Label:" with the value on the next line, and labels with extra text before the
// colon (e.g. "Contact Form Email (LPs Only) : x@y.com"). If an occurrence of the
// label has no value, keeps searching for a later occurrence of the same label
// (some labels, like "Business Hours", appear more than once in this template).
function simpleValue(lines, label) {
  const re = new RegExp('^\\*?\\s*' + escapeRegex(label) + '.*?:\\s*(.*)$', 'i');
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(re);
    if (!m) continue;
    if (m[1] && m[1].trim()) return m[1].trim();
    const collected = [];
    for (let j = i + 1; j < Math.min(lines.length, i + 6); j++) {
      const t = lines[j].trim();
      if (!t) { if (collected.length) break; else continue; }
      if (looksLikeLabel(t)) break;
      collected.push(t);
    }
    if (collected.length) return collected.join(', ');
  }
  return '';
}

// Collects all lines between an exact heading line and the next exact heading line
// (both matched after stripping a leading "*"). Searches for the end heading only
// AFTER the start, so repeated heading text earlier in the doc doesn't interfere.
function sectionByHeading(lines, startHeading, endHeadings) {
  const startNorm = startHeading.toLowerCase();
  const startIdx = lines.findIndex(l => normLine(l) === startNorm);
  if (startIdx === -1) return '';
  let endIdx = lines.length;
  for (let i = startIdx + 1; i < lines.length; i++) {
    if (endHeadings.some(h => normLine(lines[i]) === h.toLowerCase())) { endIdx = i; break; }
  }
  return lines.slice(startIdx + 1, endIdx).map(l => l.trim()).filter(Boolean).join('\n');
}

function tryParseDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function parseOnboardingSurvey(text) {
  const lines = text.split('\n').map(l => l.trim());

  const result = {
    name: simpleValue(lines, 'Client'),
    websiteUrl: simpleValue(lines, 'Website'),
    gbpLink: simpleValue(lines, 'GBP'),
    kickoff: tryParseDate(simpleValue(lines, 'Kickoff Date')),
    opsSpecialist: simpleValue(lines, 'Ops Specialist'),
    phoneNumbers: simpleValue(lines, 'Forward To Number'),
    formContactEmail: simpleValue(lines, 'Contact Form Email'),
    valueProps: simpleValue(lines, 'Value Props'),
    pmBudget: simpleValue(lines, 'Paid Media Monthly Budget'),
    lsaBudget: simpleValue(lines, 'LSA Budget'),
    searchCid: simpleValue(lines, 'Search CID'),
    adsDestinationRaw: simpleValue(lines, 'Ads Destination'),
    businessHours: simpleValue(lines, 'Business Hours'),
  };

  // Verticals: any "Vertical: X" occurrences
  const verticals = [];
  lines.forEach(l => {
    const m = l.match(/^Vertical:\s*(.+)$/i);
    if (m && m[1].trim()) verticals.push(m[1].trim());
  });
  result.verticals = [...new Set(verticals)];

  // Ads Destination — match to known options if possible
  result.adsDestination = ADS_DESTINATIONS.find(
    o => o.toLowerCase() === (result.adsDestinationRaw || '').toLowerCase()
  ) || result.adsDestinationRaw || '';

  // Platforms/channels mentioned under "Paid Media Service Channels"
  const channelsBlock = sectionByHeading(lines, 'Paid Media Service Channels', ['Ads Schedule']);
  result.channelsDetected = PLATFORMS.filter(p => channelsBlock.toLowerCase().includes(p.toLowerCase()));

  // Ad schedule (when ads run) vs. channel-specific location targeting vs. coupons
  result.adScheduleNotes = sectionByHeading(lines, 'Ads Schedule', ['Provide Channel-Specific Location Targeting']);
  const channelLoc = sectionByHeading(lines, 'Provide Channel-Specific Location Targeting', ['Coupons (LPs & Meta Only)']);
  const requestedLoc = sectionByHeading(lines, 'Location Targeting', ['Call Tracking Setup']);
  result.serviceAreaNotes = [channelLoc, requestedLoc].filter(Boolean).join('\n\n');

  result.couponsPromos = sectionByHeading(lines, 'Coupons (LPs & Meta Only)', ['LSA Onboarding']);
  result.campaignStructure = sectionByHeading(lines, 'GA Campaign Structure & Ad Groups', ['Location Targeting']);
  result.notesForBuild = sectionByHeading(lines, 'Notes For Build:', []);

  return result;
}

// ---------- UI ----------

function Row({ label, checked, onToggle, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 9px',
      borderRadius: 7, background: checked ? '#E6F1FB' : 'transparent',
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onToggle(e.target.checked)}
        style={{ marginTop: 8, width: 15, height: 15, accentColor: '#185FA5', flexShrink: 0 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>
          {label}
        </div>
        {children}
      </div>
    </div>
  );
}

const fieldInputStyle = {
  width: '100%', fontSize: 13, padding: '6px 9px',
  border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7,
  background: '#fafaf8', color: '#1a1a18', fontFamily: 'inherit', boxSizing: 'border-box',
};

export default function ImportSurveyModal({ client, onApply, onClose }) {
  const [docType, setDocType] = useState(null); // null | 'onboarding' | 'website' | 'paidMedia' | 'googleAds' | 'lsa' | 'meta'
  const [raw, setRaw] = useState('');
  const [parsed, setParsed] = useState(null);
  const [qaRaw, setQaRaw] = useState(null); // holds original pasted text for QA types (always saved for reference)
  const [values, setValues] = useState({});
  const [included, setIncluded] = useState({});
  const [platformAssign, setPlatformAssign] = useState({ pmBudget: 'Google Ads', searchCid: 'Google Ads' });

  const QA_PLATFORM_MAP = { googleAds: 'Google Ads', lsa: 'LSA', meta: 'Meta' };

  function handleParse() {
    if (docType === 'onboarding') {
      const p = parseOnboardingSurvey(raw);
      setParsed(p);
      setValues(p);
      const inc = {};
      Object.keys(p).forEach(k => { inc[k] = !!p[k] && (!Array.isArray(p[k]) || p[k].length > 0); });
      setIncluded(inc);
    } else {
      const { fields } = parseQaSurvey(docType, raw);
      setQaRaw(raw);
      setValues(fields);
      const inc = {};
      Object.keys(fields).forEach(k => { inc[k] = fields[k] !== '' && fields[k] != null; });
      setIncluded(inc);
    }
  }

  function setVal(key, v) {
    setValues(prev => ({ ...prev, [key]: v }));
  }
  function toggle(key, v) {
    setIncluded(prev => ({ ...prev, [key]: v }));
  }

  function handleApply() {
    let platforms = [...(client.platforms || [])];
    const ensurePlatform = name => {
      if (!platforms.some(p => p.name === name)) platforms.push(newPlatform(name));
      return platforms.find(p => p.name === name);
    };

    if (included.channelsDetected && values.channelsDetected?.length) {
      values.channelsDetected.forEach(name => ensurePlatform(name));
    }

    if (included.searchCid && values.searchCid) {
      const target = ensurePlatform(platformAssign.searchCid);
      target.cid = values.searchCid;
    }
    if (included.lsaBudget && values.lsaBudget) {
      const target = ensurePlatform('LSA');
      target.budget = parseFloat(String(values.lsaBudget).replace(/[^0-9.]/g, '')) || target.budget;
    }
    if (included.pmBudget && values.pmBudget) {
      const target = ensurePlatform(platformAssign.pmBudget);
      target.budget = parseFloat(String(values.pmBudget).replace(/[^0-9.]/g, '')) || target.budget;
    }

    const fields = { platforms };

    if (included.name && values.name) fields.name = values.name;
    if (included.websiteUrl && values.websiteUrl) fields.websiteUrl = values.websiteUrl;
    if (included.gbpLink && values.gbpLink) fields.gbpLink = values.gbpLink;
    if (included.kickoff && values.kickoff) fields.kickoff = values.kickoff;
    if (included.opsSpecialist && values.opsSpecialist) fields.opsSpecialist = values.opsSpecialist;
    if (included.phoneNumbers && values.phoneNumbers) fields.phoneNumbers = values.phoneNumbers;
    if (included.formContactEmail && values.formContactEmail) fields.formContactEmail = values.formContactEmail;
    if (included.valueProps && values.valueProps) fields.valueProps = values.valueProps;
    if (included.adsDestination && values.adsDestination) fields.adsDestination = values.adsDestination;
    if (included.adScheduleNotes && values.adScheduleNotes) fields.adScheduleNotes = values.adScheduleNotes;
    if (included.serviceAreaNotes && values.serviceAreaNotes) fields.serviceAreaNotes = values.serviceAreaNotes;
    if (included.couponsPromos && values.couponsPromos) fields.couponsPromos = values.couponsPromos;
    if (included.campaignStructure && values.campaignStructure) fields.campaignStructure = values.campaignStructure;
    if (included.businessHours && values.businessHours) fields.businessHours = values.businessHours;
    if (included.verticals && values.verticals?.length) {
      const existing = Array.isArray(client.verticals) ? client.verticals : [];
      fields.verticals = [...new Set([...existing, ...values.verticals])];
    }
    if (included.notesForBuild && values.notesForBuild) {
      fields.surveyNotes = (client.surveyNotes ? client.surveyNotes + '\n\n' : '') + values.notesForBuild;
    }

    onApply(fields);
    onClose();
  }

  function handleApplyQa() {
    let platforms = [...(client.platforms || [])];
    const ensurePlatform = name => {
      if (!platforms.some(p => p.name === name)) platforms.push(newPlatform(name));
      return platforms.find(p => p.name === name);
    };

    const fields = {};
    const platformName = QA_PLATFORM_MAP[docType];
    if (platformName) {
      let touched = false;
      if (included.cid && values.cid) { ensurePlatform(platformName).cid = values.cid; touched = true; }
      if (included.budget && values.budget) { ensurePlatform(platformName).budget = values.budget; touched = true; }
      if (included.accountType && values.accountType) { ensurePlatform(platformName).accountType = values.accountType; touched = true; }
      if (included.facebookPageUrl && values.facebookPageUrl) { ensurePlatform(platformName).facebookPageUrl = values.facebookPageUrl; touched = true; }
      if (included.metaPixelStatus && values.metaPixelStatus) { ensurePlatform(platformName).metaPixelStatus = values.metaPixelStatus; touched = true; }
      if (touched) fields.platforms = platforms;
    }
    if (included.websiteUrl && values.websiteUrl) fields.websiteUrl = values.websiteUrl;
    if (included.legalBusinessName && values.legalBusinessName) fields.legalBusinessName = values.legalBusinessName;
    if (included.serviceAreaNotes && values.serviceAreaNotes) {
      fields.serviceAreaNotes = client.serviceAreaNotes ? client.serviceAreaNotes + '\n\n' + values.serviceAreaNotes : values.serviceAreaNotes;
    }
    if (included.valueProps && values.valueProps) {
      fields.valueProps = client.valueProps ? client.valueProps + '\n\n' + values.valueProps : values.valueProps;
    }
    if (included.adScheduleNotes && values.adScheduleNotes) {
      fields.adScheduleNotes = client.adScheduleNotes ? client.adScheduleNotes + '\n\n' + values.adScheduleNotes : values.adScheduleNotes;
    }
    if (included.couponsPromos && values.couponsPromos) {
      fields.couponsPromos = client.couponsPromos ? client.couponsPromos + '\n\n' + values.couponsPromos : values.couponsPromos;
    }
    if (included.surveyNotes && values.surveyNotes) {
      fields.surveyNotes = client.surveyNotes ? client.surveyNotes + '\n\n' + values.surveyNotes : values.surveyNotes;
    }

    // Always keep the full survey on file for reference, regardless of which fields were applied
    fields.surveys = { ...(client.surveys || {}), [docType]: { raw: qaRaw, savedAt: Date.now() } };

    onApply(fields);
    onClose();
  }

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 260, padding: 20, overflowY: 'auto',
  };
  const modalStyle = {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 780,
    border: '1px solid rgba(0,0,0,0.15)', margin: 'auto',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        <div style={{
          padding: '13px 18px', borderBottom: '1px solid rgba(0,0,0,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
          position: 'sticky', top: 0, background: '#fff',
          borderRadius: '12px 12px 0 0', zIndex: 1,
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, flex: 1, color: '#1a1a18' }}>
            📥 Import {docType === 'onboarding' ? 'Onboarding Survey' : docType ? SURVEY_TYPES.find(t => t.id === docType)?.label : 'a Document'}
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 18, color: '#999', padding: '2px 6px',
          }}>✕</button>
        </div>

        {docType === null ? (
          <div style={{ padding: 18 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
              Which document are you importing? Each has a different question format, so pick the matching one for accurate parsing.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button onClick={() => setDocType('onboarding')} style={docTypeBtnStyle}>
                📋 Paid Media Onboarding Doc
                <span style={docTypeHintStyle}>Client name, CIDs, budgets, ad schedule, LSA info, etc.</span>
              </button>
              {SURVEY_TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => t.available && setDocType(t.id)}
                  disabled={!t.available}
                  style={{ ...docTypeBtnStyle, opacity: t.available ? 1 : 0.5, cursor: t.available ? 'pointer' : 'not-allowed' }}
                >
                  📝 {t.label}
                  {!t.available && <span style={docTypeHintStyle}>Send me this template and I'll add it</span>}
                </button>
              ))}
            </div>
          </div>
        ) : docType === 'onboarding' ? (
        !parsed ? (
          <div style={{ padding: 18 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              Paste the full text of the Paid Media Onboarding doc for this client (select all in the doc, copy, paste below).
              Note: hyperlinked words (Wrike, Contract, SF, Client Folder) paste as plain text without their link — you'll
              still need to add those links by hand.
            </div>
            <textarea
              value={raw}
              onChange={e => setRaw(e.target.value)}
              placeholder="Paste the onboarding doc text here…"
              style={{
                width: '100%', minHeight: 380, fontSize: 12, lineHeight: 1.5,
                padding: 12, border: '1px solid rgba(0,0,0,0.18)', borderRadius: 8,
                background: '#fafaf8', color: '#1a1a18', fontFamily: 'inherit',
                resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>
        ) : (
          <div style={{ padding: 18 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              Review what was detected — uncheck anything you don't want applied, and edit any value that parsed wrong before applying.
            </div>

            <Row label="Client Name" checked={included.name} onToggle={v => toggle('name', v)}>
              <input style={fieldInputStyle} value={values.name || ''} onChange={e => setVal('name', e.target.value)} />
            </Row>
            <Row label="Website URL" checked={included.websiteUrl} onToggle={v => toggle('websiteUrl', v)}>
              <input style={fieldInputStyle} value={values.websiteUrl || ''} onChange={e => setVal('websiteUrl', e.target.value)} />
            </Row>
            <Row label="GBP Link" checked={included.gbpLink} onToggle={v => toggle('gbpLink', v)}>
              <input style={fieldInputStyle} value={values.gbpLink || ''} onChange={e => setVal('gbpLink', e.target.value)} />
            </Row>
            <Row label="Kickoff Date" checked={included.kickoff} onToggle={v => toggle('kickoff', v)}>
              <input type="date" style={fieldInputStyle} value={values.kickoff || ''} onChange={e => setVal('kickoff', e.target.value)} />
            </Row>
            <Row label="Ops Specialist" checked={included.opsSpecialist} onToggle={v => toggle('opsSpecialist', v)}>
              <input style={fieldInputStyle} value={values.opsSpecialist || ''} onChange={e => setVal('opsSpecialist', e.target.value)} />
            </Row>
            <Row label="Ads Destination" checked={included.adsDestination} onToggle={v => toggle('adsDestination', v)}>
              <select style={fieldInputStyle} value={values.adsDestination || ''} onChange={e => setVal('adsDestination', e.target.value)}>
                <option value="">— none detected —</option>
                {ADS_DESTINATIONS.map(o => <option key={o}>{o}</option>)}
              </select>
            </Row>
            <Row label="Verticals" checked={included.verticals} onToggle={v => toggle('verticals', v)}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {(values.verticals || []).map(v => (
                  <span key={v} style={{
                    fontSize: 12, padding: '3px 10px', borderRadius: 20,
                    background: VERTICALS.includes(v) ? '#E6F1FB' : '#EEEDFE',
                    color: VERTICALS.includes(v) ? '#185FA5' : '#3C3489', fontWeight: 700,
                  }}>{v}</span>
                ))}
                {(!values.verticals || values.verticals.length === 0) && <span style={{ fontSize: 12, color: '#aaa' }}>none detected</span>}
              </div>
            </Row>
            <Row label="Channels / Platforms to add" checked={included.channelsDetected} onToggle={v => toggle('channelsDetected', v)}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {PLATFORMS.map(p => {
                  const active = (values.channelsDetected || []).includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setVal('channelsDetected', active
                        ? values.channelsDetected.filter(x => x !== p)
                        : [...(values.channelsDetected || []), p])}
                      style={{
                        fontSize: 12, padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                        border: active ? '2px solid #185FA5' : '2px solid rgba(0,0,0,0.15)',
                        background: active ? '#E6F1FB' : '#f5f5f3',
                        color: active ? '#185FA5' : '#888', fontWeight: 700, fontFamily: 'inherit',
                      }}
                    >{p}</button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Already-existing platforms on this client won't be duplicated.</div>
            </Row>

            <Row label="Search CID" checked={included.searchCid} onToggle={v => toggle('searchCid', v)}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...fieldInputStyle, flex: 1 }} value={values.searchCid || ''} onChange={e => setVal('searchCid', e.target.value)} />
                <select style={{ ...fieldInputStyle, width: 140 }} value={platformAssign.searchCid} onChange={e => setPlatformAssign(a => ({ ...a, searchCid: e.target.value }))}>
                  {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Goes onto the selected platform's CID field</div>
            </Row>

            <Row label="Paid Media Monthly Budget" checked={included.pmBudget} onToggle={v => toggle('pmBudget', v)}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...fieldInputStyle, flex: 1 }} value={values.pmBudget || ''} onChange={e => setVal('pmBudget', e.target.value)} />
                <select style={{ ...fieldInputStyle, width: 140 }} value={platformAssign.pmBudget} onChange={e => setPlatformAssign(a => ({ ...a, pmBudget: e.target.value }))}>
                  {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Sets the budget field on the selected platform</div>
            </Row>
            <Row label="LSA Budget" checked={included.lsaBudget} onToggle={v => toggle('lsaBudget', v)}>
              <input style={fieldInputStyle} value={values.lsaBudget || ''} onChange={e => setVal('lsaBudget', e.target.value)} />
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Sets the LSA platform's budget (LSA will be added if not already present)</div>
            </Row>

            <Row label="Forward-To Phone Number" checked={included.phoneNumbers} onToggle={v => toggle('phoneNumbers', v)}>
              <input style={fieldInputStyle} value={values.phoneNumbers || ''} onChange={e => setVal('phoneNumbers', e.target.value)} />
            </Row>
            <Row label="Contact Form Email" checked={included.formContactEmail} onToggle={v => toggle('formContactEmail', v)}>
              <input style={fieldInputStyle} value={values.formContactEmail || ''} onChange={e => setVal('formContactEmail', e.target.value)} />
            </Row>
            <Row label="Ad Schedule" checked={included.adScheduleNotes} onToggle={v => toggle('adScheduleNotes', v)}>
              <textarea style={{ ...fieldInputStyle, minHeight: 60 }} value={values.adScheduleNotes || ''} onChange={e => setVal('adScheduleNotes', e.target.value)} />
            </Row>
            <Row label="Business Hours" checked={included.businessHours} onToggle={v => toggle('businessHours', v)}>
              <input style={fieldInputStyle} value={values.businessHours || ''} onChange={e => setVal('businessHours', e.target.value)} />
            </Row>
            <Row label="Service Area / Location Targeting" checked={included.serviceAreaNotes} onToggle={v => toggle('serviceAreaNotes', v)}>
              <textarea style={{ ...fieldInputStyle, minHeight: 90 }} value={values.serviceAreaNotes || ''} onChange={e => setVal('serviceAreaNotes', e.target.value)} />
            </Row>
            <Row label="Coupons / Promos" checked={included.couponsPromos} onToggle={v => toggle('couponsPromos', v)}>
              <textarea style={{ ...fieldInputStyle, minHeight: 60 }} value={values.couponsPromos || ''} onChange={e => setVal('couponsPromos', e.target.value)} />
            </Row>
            <Row label="Campaign Structure" checked={included.campaignStructure} onToggle={v => toggle('campaignStructure', v)}>
              <textarea style={{ ...fieldInputStyle, minHeight: 90 }} value={values.campaignStructure || ''} onChange={e => setVal('campaignStructure', e.target.value)} />
            </Row>
            <Row label="Value Props" checked={included.valueProps} onToggle={v => toggle('valueProps', v)}>
              <textarea style={{ ...fieldInputStyle, minHeight: 60 }} value={values.valueProps || ''} onChange={e => setVal('valueProps', e.target.value)} />
            </Row>
            <Row label="Notes For Build → appended to Survey Notes" checked={included.notesForBuild} onToggle={v => toggle('notesForBuild', v)}>
              <textarea style={{ ...fieldInputStyle, minHeight: 60 }} value={values.notesForBuild || ''} onChange={e => setVal('notesForBuild', e.target.value)} />
            </Row>
          </div>
        )
        ) : (
          !qaRaw ? (
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                Paste the full text of the {SURVEY_TYPES.find(t => t.id === docType)?.label} for this client. The whole thing
                will be saved on this client card for reference, and a few key fields will be offered for autofill below.
              </div>
              <textarea
                value={raw}
                onChange={e => setRaw(e.target.value)}
                placeholder="Paste the survey text here…"
                style={{
                  width: '100%', minHeight: 380, fontSize: 12, lineHeight: 1.5,
                  padding: 12, border: '1px solid rgba(0,0,0,0.18)', borderRadius: 8,
                  background: '#fafaf8', color: '#1a1a18', fontFamily: 'inherit',
                  resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>
          ) : (
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                The full survey will be saved on this client card either way — reviewed below are just the fields that can
                autofill onto the client card. Uncheck anything you don't want applied.
              </div>

              {docType === 'website' && (
                <>
                  <Row label="Legal Business Name (reference)" checked={included.legalBusinessName} onToggle={v => toggle('legalBusinessName', v)}>
                    <input style={fieldInputStyle} value={values.legalBusinessName || ''} onChange={e => setVal('legalBusinessName', e.target.value)} />
                  </Row>
                  <Row label="Website URL" checked={included.websiteUrl} onToggle={v => toggle('websiteUrl', v)}>
                    <input style={fieldInputStyle} value={values.websiteUrl || ''} onChange={e => setVal('websiteUrl', e.target.value)} />
                  </Row>
                  <Row label="Service Area" checked={included.serviceAreaNotes} onToggle={v => toggle('serviceAreaNotes', v)}>
                    <textarea style={{ ...fieldInputStyle, minHeight: 70 }} value={values.serviceAreaNotes || ''} onChange={e => setVal('serviceAreaNotes', e.target.value)} />
                  </Row>
                  <Row label="Value Props" checked={included.valueProps} onToggle={v => toggle('valueProps', v)}>
                    <textarea style={{ ...fieldInputStyle, minHeight: 60 }} value={values.valueProps || ''} onChange={e => setVal('valueProps', e.target.value)} />
                  </Row>
                  <Row label="Survey Notes (kept separate from Notes/Pinned Note)" checked={included.surveyNotes} onToggle={v => toggle('surveyNotes', v)}>
                    <textarea style={{ ...fieldInputStyle, minHeight: 50 }} value={values.surveyNotes || ''} onChange={e => setVal('surveyNotes', e.target.value)} />
                  </Row>
                </>
              )}

              {docType === 'paidMedia' && (
                <>
                  <Row label="Ad Schedule" checked={included.adScheduleNotes} onToggle={v => toggle('adScheduleNotes', v)}>
                    <textarea style={{ ...fieldInputStyle, minHeight: 60 }} value={values.adScheduleNotes || ''} onChange={e => setVal('adScheduleNotes', e.target.value)} />
                  </Row>
                  <Row label="Service Area / Location Targeting" checked={included.serviceAreaNotes} onToggle={v => toggle('serviceAreaNotes', v)}>
                    <textarea style={{ ...fieldInputStyle, minHeight: 60 }} value={values.serviceAreaNotes || ''} onChange={e => setVal('serviceAreaNotes', e.target.value)} />
                  </Row>
                  <Row label="Coupons / Promos" checked={included.couponsPromos} onToggle={v => toggle('couponsPromos', v)}>
                    <textarea style={{ ...fieldInputStyle, minHeight: 50 }} value={values.couponsPromos || ''} onChange={e => setVal('couponsPromos', e.target.value)} />
                  </Row>
                  <Row label="Survey Notes (kept separate from Notes/Pinned Note)" checked={included.surveyNotes} onToggle={v => toggle('surveyNotes', v)}>
                    <textarea style={{ ...fieldInputStyle, minHeight: 50 }} value={values.surveyNotes || ''} onChange={e => setVal('surveyNotes', e.target.value)} />
                  </Row>
                </>
              )}

              {(docType === 'googleAds' || docType === 'lsa' || docType === 'meta') && (
                <>
                  <Row label={`${QA_PLATFORM_MAP[docType]} CID / Account ID`} checked={included.cid} onToggle={v => toggle('cid', v)}>
                    <input style={fieldInputStyle} value={values.cid || ''} onChange={e => setVal('cid', e.target.value)} />
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                      {PLATFORMS.includes(QA_PLATFORM_MAP[docType]) ? `Goes onto the client's ${QA_PLATFORM_MAP[docType]} platform (added if not already present)` : ''}
                    </div>
                  </Row>
                  <Row label="Budget" checked={included.budget} onToggle={v => toggle('budget', v)}>
                    <input style={fieldInputStyle} value={values.budget ?? ''} onChange={e => setVal('budget', e.target.value)} />
                    {!values.budget && <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Client didn't give a specific number — nothing to apply here.</div>}
                  </Row>
                  <Row label="Account Type" checked={included.accountType} onToggle={v => toggle('accountType', v)}>
                    <select style={fieldInputStyle} value={values.accountType || ''} onChange={e => setVal('accountType', e.target.value)}>
                      <option value="">— not detected —</option>
                      <option value="existing">Existing</option>
                      <option value="new">New</option>
                    </select>
                  </Row>
                  {docType === 'meta' && (
                    <>
                      <Row label="Facebook Page URL" checked={included.facebookPageUrl} onToggle={v => toggle('facebookPageUrl', v)}>
                        <input style={fieldInputStyle} value={values.facebookPageUrl || ''} onChange={e => setVal('facebookPageUrl', e.target.value)} />
                      </Row>
                      <Row label="Meta Pixel Status" checked={included.metaPixelStatus} onToggle={v => toggle('metaPixelStatus', v)}>
                        <select style={fieldInputStyle} value={values.metaPixelStatus || ''} onChange={e => setVal('metaPixelStatus', e.target.value)}>
                          <option value="">— not detected —</option>
                          <option value="installed">Installed</option>
                          <option value="not_installed">Not Installed</option>
                          <option value="unsure">Client Unsure</option>
                        </select>
                      </Row>
                    </>
                  )}
                  <Row label="Survey Notes (kept separate from Notes/Pinned Note)" checked={included.surveyNotes} onToggle={v => toggle('surveyNotes', v)}>
                    <textarea style={{ ...fieldInputStyle, minHeight: 60 }} value={values.surveyNotes || ''} onChange={e => setVal('surveyNotes', e.target.value)} />
                  </Row>
                </>
              )}
            </div>
          )
        )}

        <div style={{
          padding: '12px 18px', borderTop: '1px solid rgba(0,0,0,0.1)',
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          position: 'sticky', bottom: 0, background: '#fff',
          borderRadius: '0 0 12px 12px', gap: 8,
        }}>
          {docType !== null && (
            <button onClick={() => { setDocType(null); setParsed(null); setQaRaw(null); setRaw(''); }} style={{
              fontSize: 12, padding: '7px 14px', borderRadius: 7,
              background: '#f5f5f3', color: '#333', border: '1px solid rgba(0,0,0,0.18)',
              cursor: 'pointer', fontFamily: 'inherit', marginRight: 'auto',
            }}>
              ← Choose different document
            </button>
          )}
          <button onClick={onClose} style={{
            fontSize: 12, padding: '7px 14px', borderRadius: 7,
            background: '#f5f5f3', color: '#333', border: '1px solid rgba(0,0,0,0.18)',
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Cancel
          </button>
          {docType === null ? null : docType === 'onboarding' ? (
            !parsed ? (
              <button onClick={handleParse} disabled={!raw.trim()} style={{
                fontSize: 12, padding: '7px 18px', borderRadius: 7,
                background: raw.trim() ? '#185FA5' : '#aaa', color: '#fff', border: 'none',
                cursor: raw.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', fontWeight: 700,
              }}>
                Parse Document →
              </button>
            ) : (
              <button onClick={handleApply} style={{
                fontSize: 12, padding: '7px 18px', borderRadius: 7,
                background: '#185FA5', color: '#fff', border: 'none',
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              }}>
                ✓ Apply to Client Card
              </button>
            )
          ) : (
            !qaRaw ? (
              <button onClick={handleParse} disabled={!raw.trim()} style={{
                fontSize: 12, padding: '7px 18px', borderRadius: 7,
                background: raw.trim() ? '#185FA5' : '#aaa', color: '#fff', border: 'none',
                cursor: raw.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', fontWeight: 700,
              }}>
                Parse Document →
              </button>
            ) : (
              <button onClick={handleApplyQa} style={{
                fontSize: 12, padding: '7px 18px', borderRadius: 7,
                background: '#185FA5', color: '#fff', border: 'none',
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              }}>
                ✓ Save & Apply to Client Card
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

const docTypeBtnStyle = {
  textAlign: 'left', padding: '10px 14px', borderRadius: 8,
  border: '1px solid rgba(0,0,0,0.15)', background: '#fafaf8',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: '#1a1a18',
  display: 'flex', flexDirection: 'column', gap: 2,
};
const docTypeHintStyle = { fontSize: 11, fontWeight: 400, color: '#999' };
